import { css } from 'aphrodite';
import * as React from 'react';
import { useCoachingAdherenceScoreboardQuery } from '../../../queries/AIConversation/useCoachingAdherenceScoreboardQuery';
import { useSalesCoachingCohortsQuery } from '../../../queries/AISalesCoaching/useSalesCoachingCohortsQuery';
import { DateInput } from '../../../web/components-v2/DateInput';
import { Field, FieldLabel } from '../../../web/components-v2/Field';
import { IUser } from '../../../extViewmodels';
import { ComboboxContent, ComboboxInput, ComboboxList } from '../../../web/components-v2/Combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../web/components-v2/Select';
import { UserCombobox, UserComboboxAvatarItem, UserComboboxStatus } from '../../../web/components/UserCombobox';
import { LoadingSpinner } from '../../../web/components/LoadingSpinner';
import {
	type GroupFilterOption,
	MIN_CALLS_FOR_SHARE,
	NO_GROUP_FILTERS,
	type RepGroupFilters,
	applyGroupFilters,
	buildScoreboardRange,
	formatPercent,
	formatRate,
	groupFilterOptions,
	hasGroupFilters,
	headlineText,
	repHeadlineText,
	summarizeScoreboard,
	todayDateString,
} from './scoreboardMetrics';
import { styleSheet } from './scoreboard.styles';
import { ScoreboardTable } from './ScoreboardTable';
import { usePersistedScoreboardCohort } from './usePersistedScoreboardCohort';

interface IProps {
	onSelectCall: (aiConversationId: string) => void;
}

// Radix Select reserves the empty string for "no value", so "any" needs a real one.
const ANY = '__any__';

function GroupFilterSelect({
	id,
	label,
	anyLabel,
	value,
	options,
	onChange,
}: {
	id: string;
	label: string;
	anyLabel: string;
	value: string;
	options: GroupFilterOption[];
	onChange: (value: string) => void;
}) {
	return (
		<Field styles={[styleSheet.controlField]}>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			<Select value={value || ANY} onValueChange={next => onChange(next === ANY ? '' : next)}>
				<SelectTrigger id={id} styles={styleSheet.groupTrigger}>
					<SelectValue>{options.find(option => option.value === value)?.label ?? anyLabel}</SelectValue>
				</SelectTrigger>
				<SelectContent>
					<SelectItem value={ANY}>{anyLabel}</SelectItem>
					{options.map(option => (
						<SelectItem key={option.value} value={option.value}>
							{option.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
		</Field>
	);
}

export function AdherenceScoreboard({ onSelectCall }: IProps) {
	const [storedCohort, setCohort] = usePersistedScoreboardCohort();
	const [startDate, setStartDate] = React.useState(todayDateString);
	const [endDate, setEndDate] = React.useState(todayDateString);
	const [pickedRep, setPickedRep] = React.useState<IUser | null>(null);
	const [groupFilters, setGroupFilters] = React.useState<RepGroupFilters>(NO_GROUP_FILTERS);

	const cohortsQuery = useSalesCoachingCohortsQuery();
	const cohorts = [
		...new Set((cohortsQuery.data ?? []).map(member => member.cohort).filter((name): name is string => Boolean(name))),
	].sort();
	const cohort = storedCohort && cohorts.includes(storedCohort) ? storedCohort : (cohorts[0] ?? null);
	const pickedRepCohort = pickedRep
		? ((cohortsQuery.data ?? []).find(member => member.userId === pickedRep.id)?.cohort ?? null)
		: null;

	// The board is one cohort at a time, so picking a rep moves to their cohort and picking a cohort drops the rep and
	// the group filters. A rep and a group filter would contradict each other, so choosing one clears the other.
	const onRepChange = (user: IUser | null) => {
		setPickedRep(user);
		setGroupFilters(NO_GROUP_FILTERS);
		const repCohort = user ? (cohortsQuery.data ?? []).find(member => member.userId === user.id)?.cohort : null;
		if (repCohort) {
			setCohort(repCohort);
		}
	};
	const onCohortChange = (next: string) => {
		setCohort(next);
		setPickedRep(null);
		setGroupFilters(NO_GROUP_FILTERS);
	};
	const onGroupFilterChange = (change: Partial<RepGroupFilters>) => {
		setGroupFilters(current => ({ ...current, ...change }));
		setPickedRep(null);
	};

	const range = buildScoreboardRange(startDate, endDate);
	const validRange = 'error' in range ? null : range;
	const rangeError = 'error' in range ? range.error : null;
	const scoreboardQuery = useCoachingAdherenceScoreboardQuery({
		cohort,
		start: validRange?.start ?? null,
		end: validRange?.end ?? null,
	});

	const selectedRep = pickedRep ? (scoreboardQuery.data?.reps.find(rep => rep.userId === pickedRep.id) ?? null) : null;
	const options = groupFilterOptions(scoreboardQuery.data?.reps ?? []);
	// A new range rebuilds the options, so a value they no longer offer would filter invisibly; it's dropped instead.
	const offered = (value: string, list: GroupFilterOption[]) =>
		list.some(option => option.value === value) ? value : '';
	const activeFilters: RepGroupFilters = {
		managerUserId: offered(groupFilters.managerUserId, options.managers),
		quotaTeam: offered(groupFilters.quotaTeam, options.quotaTeams),
		role: offered(groupFilters.role, options.roles),
	};
	const grouped = hasGroupFilters(activeFilters);

	const renderBody = () => {
		if (cohortsQuery.isLoading || (validRange && cohort && scoreboardQuery.isLoading)) {
			return <LoadingSpinner type='large' />;
		}
		if (cohortsQuery.isError) {
			return <div className={css(styleSheet.state)}>Couldn&rsquo;t load coaching cohorts.</div>;
		}
		if (!cohort) {
			return <div className={css(styleSheet.state)}>No reps are in a coaching cohort yet.</div>;
		}
		if (pickedRep && !pickedRepCohort) {
			const name = `${pickedRep.firstName ?? ''} ${pickedRep.lastName ?? ''}`.trim() || 'This rep';
			return (
				<div className={css(styleSheet.state)}>
					{name} isn&rsquo;t in a coaching cohort, so they aren&rsquo;t on the scoreboard.
				</div>
			);
		}
		if (!validRange) {
			return null;
		}
		if (scoreboardQuery.isError || !scoreboardQuery.data) {
			return <div className={css(styleSheet.state)}>Couldn&rsquo;t load the adherence scoreboard.</div>;
		}
		const scoreboard = scoreboardQuery.data;
		if (scoreboard.reps.every(rep => rep.judgedCalls === 0)) {
			return (
				<div className={css(styleSheet.state)}>
					No calls were scored by the adherence judge in this range yet. A call is scored a few minutes after the rep
					logs its outcome.
				</div>
			);
		}
		if (pickedRep && !selectedRep) {
			const name = `${pickedRep.firstName ?? ''} ${pickedRep.lastName ?? ''}`.trim() || 'This rep';
			return (
				<div className={css(styleSheet.state)}>
					{name} isn&rsquo;t on this cohort&rsquo;s scoreboard for this range.
				</div>
			);
		}
		const reps = selectedRep ? [selectedRep] : applyGroupFilters(scoreboard.reps, activeFilters);
		if (reps.length === 0) {
			return <div className={css(styleSheet.state)}>No reps in this cohort match these filters.</div>;
		}
		const summary = summarizeScoreboard(reps);
		const whose = selectedRep ? 'their' : grouped ? 'these reps’' : 'the cohort’s';
		return (
			<>
				<div className={css(styleSheet.headline)}>
					<p className={css(styleSheet.headlineMain)}>
						{selectedRep ? repHeadlineText(selectedRep) : headlineText(summary)}
					</p>
					<p className={css(styleSheet.headlineDetail)}>
						{formatPercent(summary.cohortShare)} of {whose} calls past the hook followed the coach. Demos per
						hook-accepted call: {formatRate(summary.demosPerFollowedCall)} when the rep followed the coach,{' '}
						{formatRate(summary.demosPerPastHookCall)} across all calls past the hook.
					</p>
					<p className={css(styleSheet.headlineNote)}>
						A call counts as followed at a score of {scoreboard.followedScore} or more. Rep counts include only reps
						with at least {MIN_CALLS_FOR_SHARE} calls past the hook.
					</p>
				</div>
				<ScoreboardTable
					key={selectedRep?.userId ?? 'all'}
					scoreboard={scoreboard}
					reps={reps}
					initialExpandedRepId={selectedRep?.userId ?? null}
					range={validRange}
					onSelectCall={onSelectCall}
				/>
			</>
		);
	};

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.controls)}>
				<Field styles={[styleSheet.controlField]}>
					<FieldLabel htmlFor='scoreboard-cohort'>Cohort</FieldLabel>
					<Select value={cohort ?? ''} onValueChange={onCohortChange} disabled={cohorts.length === 0}>
						<SelectTrigger id='scoreboard-cohort' styles={styleSheet.cohortTrigger}>
							<SelectValue placeholder='No cohorts'>{cohort}</SelectValue>
						</SelectTrigger>
						<SelectContent>
							{cohorts.map(name => (
								<SelectItem key={name} value={name}>
									{name}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</Field>
				<Field styles={[styleSheet.controlField]}>
					<FieldLabel htmlFor='scoreboard-rep'>Rep</FieldLabel>
					<UserCombobox
						id={pickedRep?.id}
						key={`rep-${pickedRep?.id ?? ''}`}
						onValueChange={(user: IUser | null) => onRepChange(user)}
					>
						<ComboboxInput
							id='scoreboard-rep'
							placeholder={cohortsQuery.data ? 'All reps' : 'Loading reps…'}
							showClear
							disabled={!cohortsQuery.data}
							styles={[styleSheet.cohortTrigger]}
						/>
						<ComboboxContent>
							<UserComboboxStatus />
							<ComboboxList>
								{(user: IUser) => <UserComboboxAvatarItem key={`rep-item-${user.id}`} user={user} />}
							</ComboboxList>
						</ComboboxContent>
					</UserCombobox>
				</Field>
				{options.managers.length > 0 ? (
					<GroupFilterSelect
						id='scoreboard-manager'
						label='Manager'
						anyLabel='All managers'
						value={activeFilters.managerUserId}
						options={options.managers}
						onChange={managerUserId => onGroupFilterChange({ managerUserId })}
					/>
				) : null}
				{options.quotaTeams.length > 0 ? (
					<GroupFilterSelect
						id='scoreboard-quota-team'
						label='Quota team'
						anyLabel='All teams'
						value={activeFilters.quotaTeam}
						options={options.quotaTeams}
						onChange={quotaTeam => onGroupFilterChange({ quotaTeam })}
					/>
				) : null}
				{options.roles.length > 0 ? (
					<GroupFilterSelect
						id='scoreboard-role'
						label='Role'
						anyLabel='All roles'
						value={activeFilters.role}
						options={options.roles}
						onChange={role => onGroupFilterChange({ role })}
					/>
				) : null}
				<Field styles={[styleSheet.controlField]}>
					<FieldLabel htmlFor='scoreboard-start'>Start</FieldLabel>
					<DateInput id='scoreboard-start' value={startDate} onChange={setStartDate} />
				</Field>
				<Field styles={[styleSheet.controlField]}>
					<FieldLabel htmlFor='scoreboard-end'>End</FieldLabel>
					<DateInput id='scoreboard-end' value={endDate} onChange={setEndDate} />
				</Field>
			</div>
			{rangeError ? (
				<div className={css(styleSheet.rangeError)} role='alert'>
					{rangeError}
				</div>
			) : null}
			{renderBody()}
		</div>
	);
}
